import type { FC } from "react";
import { useEffect, useState, useRef } from "react";
import { Logs } from "@src/core/services";
import { useQuery } from "@tanstack/react-query";
import {
  DatePickerWrapper,
  ListItem,
  LogContainer,
  LogCount,
  LogHeader,
  LogDate,
  LogName,
  LogsList,
  StyledMenu,
  MenuLabel,
  MenuHeader,
  DatePickerGroup,
  MenuFooter,
  MenuFooterButtonGroup,
  ButtonWrapper,
  CenterBlock,
} from "./log-component.styles";
import { logDate } from "@src/core/utils";
import { ReactComponent as DatePickerIcon } from "@src/core/icons/date-picker.svg";
import { Button } from "@mui/material";
import { CustomDatePicker } from "@src/components";
import { FilteredLogs, LogType } from "@src/core/types";
import { Controller, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { filteredLogsSchema } from "@src/core/schemas";
import { Button as CustomButton } from "@src/components/ui";
import { Link, useParams } from "react-router-dom";
import { LogComponentProps } from "./log-component.types";
import { APP_ROUTES } from "@src/core/constants";

export const LogComponent: FC<LogComponentProps> = ({
  formType,
  formMethod,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { routeId } = useParams();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [logArr, setLogArr] = useState<LogType[]>([]);
  const prevRoute = useRef(routeId);

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ["logs", routeId],
    queryFn: () =>
      Logs.getAll({
        routeId,
      }),
  });

  const {
    handleSubmit,
    control,
    getValues,
  } = useForm<FilteredLogs>({
    mode: "onChange",
    resolver: joiResolver(filteredLogsSchema),
    defaultValues: {
      startDate: null,
      endDate: null,
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess) {
      if (logArr && logArr.length === 0) {
        setLogArr(data.data.logs);
      } else if (logArr.length === data.data.all) {
        return;
      } else if (routeId !== prevRoute.current) {
        setLogArr(data.data.logs);
      } else {
        setLogArr((prevArr) => prevArr.concat(data.data.logs));
      }
      setOffset((prevOffset) => prevOffset + 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSuccess, data, routeId]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSubmit = (data: FilteredLogs) => {
    console.log(data);
  };

  const getMoreLogs = () => {
    Logs.getAll({
      routeId,
      offset,
    }).then((response) => {
      const newData = response.data.logs;
      setLogArr((prevArr) => prevArr.concat(newData));
      setOffset((prevOffset) => prevOffset + 10);
    });
  };

  return (
    <LogContainer formType={formType} formMethod={formMethod}>
      <LogHeader>
        <div>Журнал</div>
        <ButtonWrapper>
          <Button
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup={true}
            aria-expanded={open ? true : undefined}
            onClick={handleClick}
          >
            <DatePickerWrapper>
              <DatePickerIcon />
            </DatePickerWrapper>
          </Button>
        </ButtonWrapper>
        <StyledMenu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <div>
            <MenuLabel>Выбор периода</MenuLabel>
            <MenuHeader>Введите даты</MenuHeader>
            <form onSubmit={handleSubmit(onSubmit)}>
              <DatePickerGroup>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field: { onChange, value } }) => (
                    <CustomDatePicker
                      label="Start date"
                      value={value || null}
                      onChange={onChange}
                      shouldDisableYear={(date) => {
                        const endDate = getValues(`endDate`);
                        if (!endDate) return false;
                        return date.getDate() > endDate.getDate();
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field: { onChange, value } }) => (
                    <CustomDatePicker
                      label="End date"
                      value={value || null}
                      onChange={onChange}
                      shouldDisableYear={(date) => {
                        const startDate = getValues(`startDate`);
                        if (!startDate) return true;
                        return date.getDate() < new Date(startDate).getDate();
                      }}
                    />
                  )}
                />
              </DatePickerGroup>
              <MenuFooter>
                <MenuFooterButtonGroup>
                  <CustomButton
                    text="Отмена"
                    variant="text"
                    borderradius="XXXL"
                  />
                  <CustomButton
                    type="submit"
                    text="ОК"
                    variant="text"
                    borderradius="XXXL"
                  />
                </MenuFooterButtonGroup>
              </MenuFooter>
            </form>
          </div>
        </StyledMenu>
      </LogHeader>
      <LogCount>Записи {data?.data.all}</LogCount>
      <LogsList>
        {0 !== data?.data.all && logArr?.length !== 0 &&
          logArr &&
          logArr.map((item) => (
            <Link to={`${APP_ROUTES.LOG}/${item.id}`} key={item.id}>
              <ListItem>
                <LogName>Log entry #{item.id}</LogName>
                <LogDate>
                  <div>
                    {logDate(item.time).date} {logDate(item.time).time}
                  </div>
                </LogDate>
              </ListItem>
            </Link>
          ))}
      </LogsList>
      {0 === data?.data.all || isLoading || logArr.length === data?.data.all ? null : (
        <CenterBlock>
          <CustomButton
            text="Показать еще"
            variant="text"
            borderradius="XXL"
            onClick={getMoreLogs}
          />
        </CenterBlock>
      )}
    </LogContainer>
  );
};
